import { useSelector } from 'react-redux';
import { RootState } from 'Store/index';
import { Addon, Feature } from 'Store/actions/FeatureHelperActions';
import { useFeatureAvailable } from 'App/components/hooks';

type ContactScraperRoleConfig = {
	filterEnabled: boolean;
	allowedRoleIds: number[];
};

export const parseContactScraperRoleFilter = (configString: string | null | undefined): ContactScraperRoleConfig => {
	const defaultConfig: ContactScraperRoleConfig = {
		filterEnabled: false,
		allowedRoleIds: []
	};

	if (!configString || configString === '{}') {
		return defaultConfig;
	}

	try {
		const parsed = JSON.parse(configString);

		if (typeof parsed !== 'object' || parsed === null) {
			return defaultConfig;
		}

		const filterEnabled = typeof parsed.filterEnabled === 'boolean' ? parsed.filterEnabled : false;

		let allowedRoleIds: number[] = [];
		if (Array.isArray(parsed.allowedRoleIds)) {
			allowedRoleIds = parsed.allowedRoleIds.filter((id: unknown) => typeof id === 'number' && id > 0);
		}

		return {
			filterEnabled,
			allowedRoleIds
		};
	} catch {
		return defaultConfig;
	}
};

/**
 * Pure function to check contact scraper access based on role filtering.
 * Assumes addon check is already done.
 */
export const checkContactScraperRoleAccess = (opts: {
	configString: string | null | undefined;
	isAdmin: boolean;
	userRoleId: number | null | undefined;
}): boolean => {
	const config = parseContactScraperRoleFilter(opts.configString);

	if (!config.filterEnabled) {
		return true;
	}

	if (opts.isAdmin) {
		return true;
	}

	if (config.allowedRoleIds.length === 0) {
		return false;
	}

	if (!opts.userRoleId) {
		return false;
	}

	return config.allowedRoleIds.includes(opts.userRoleId);
};

/**
 * Hook to check if the current user has access to the Contact Scraper feature.
 * Checks both addon access and role-based restrictions.
 */
export const useContactScraperAccess = (): { hasAccess: boolean } => {
	const hasAddon = Tools.FeatureHelper.isAvailableAddon(Addon.AI_CONTACT_SCRAPER);
	const hasAiAssistant = useFeatureAvailable(Feature.AI_ASSISTANT);

	const self = useSelector((state: RootState) => state.App.self);
	const params = useSelector((state: RootState) => state.App.metadata?.params);

	// Access = addon OR Upsales AI tier (mirrors canUseContactScraper on the
	// backend). The role filter below still restricts which users may run it.
	if (!hasAddon && !hasAiAssistant) {
		return { hasAccess: false };
	}

	const hasAccess = checkContactScraperRoleAccess({
		configString: params?.ContactScraperRoleFilter as string | undefined,
		isAdmin: !!self?.administrator,
		userRoleId: self?.role?.id
	});

	return { hasAccess };
};

export default useContactScraperAccess;
